import { messageService } from '@services/message.service';
import { message } from 'antd';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../rootReducer';

import {
  getConversations,
  getConversationsSuccess,
  getConversationsFail,
  setActiveConversation,
  setActiveConversationSuccess,
  getConversationDetail,
  getConversationDetailSuccess,
  fetchingMessage,
  loadMessages,
  loadMessagesSuccess,
  loadMoreMessagesSuccess,
  sendMessage,
  sendMessageSuccess,
  sendMessageFail,
  deletePrivateMessage,
  deletePrivateMessageSuccess,
  deletePrivateMessageFail,
  readMessages
} from './messageSlice';

function* getConversationsSaga(action: PayloadAction<Record<string, any>>) {
  try {
    const resp = yield call([messageService, 'getConversations'], action.payload);
    yield put(getConversationsSuccess({ ...resp.data, isMore: action.payload.isMore }));
  } catch (e) {
    const error = yield Promise.resolve(e);
    yield put(getConversationsFail(error));
  }
}

function* setActiveConversationSaga(action: PayloadAction<{ source: string; sourceId: string; conversationId: string }>) {
  try {
    const { source, sourceId, conversationId } = action.payload;
    let conversation;

    if (!conversationId) {
      const resp = yield call([messageService, 'createConversation'], {
        source,
        sourceId,
        type: 'private'
      });
      conversation = resp.data;
    } else {
      const state: RootState = yield select();
      conversation = state.message.conversations.mapping[conversationId];
    }

    yield put(setActiveConversationSuccess(conversation));
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred');
  }
}

function* getConversationDetailSaga(action: PayloadAction<string>) {
  try {
    const resp = yield messageService.getConversationDetail(action.payload);
    yield put(getConversationDetailSuccess(resp.data));
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred');
  }
}

function* loadMessagesSaga(action: PayloadAction<{ conversationId: string; limit: number; offset: number; isLoadMore?: boolean }>) {
  try {
    const { conversationId, limit, offset, isLoadMore } = action.payload;
    yield put(fetchingMessage(conversationId));

    const resp = yield messageService.getMessages(conversationId, {
      limit,
      offset,
      sort: 'createdAt',
      sortType: 'desc'
    });

    if (isLoadMore) {
      yield put(loadMoreMessagesSuccess({ conversationId, items: resp.data.data }));
    } else {
      yield put(loadMessagesSuccess({ conversationId, items: resp.data.data }));
    }
  } catch (e) {
    const error = yield Promise.resolve(e);
    message.error(error?.message || 'An error occurred loading messages');
  }
}

function* sendMessageSaga(action: PayloadAction<any>) {
  try {
    const { conversationId, ...data } = action.payload;
    const resp = yield messageService.sendMessage(conversationId, data);
    yield put(sendMessageSuccess({
      conversationId,
      message: resp.data
    }));
  } catch (e) {
    const error = yield Promise.resolve(e);
    yield put(sendMessageFail(error));
    message.error(error?.message || 'An error occurred sending message');
  }
}

function* deletePrivateMessageSaga(action: PayloadAction<string>) {
  try {
    yield messageService.deleteMessage(action.payload);
    yield put(deletePrivateMessageSuccess({ messageId: action.payload }));
    message.success('Message deleted successfully');
  } catch (e) {
    const error = yield Promise.resolve(e);
    yield put(deletePrivateMessageFail(error));
    message.error(error?.message || 'An error occurred deleting message');
  }
}

function* readMessagesSaga(action: PayloadAction<string>) {
  try {
    const conversationId = action.payload;
    yield messageService.readAllInConversation(conversationId);
    // The readMessages action already updates the local state,
    // so we don't need to dispatch another action here
  } catch (e) {
    // Silently fail - reading messages is not critical
    console.error('Error marking messages as read:', e);
  }
}

export default function* messageSaga() {
  yield takeLatest(getConversations.type, getConversationsSaga);
  yield takeLatest(setActiveConversation.type, setActiveConversationSaga);
  yield takeLatest(getConversationDetail.type, getConversationDetailSaga);
  yield takeLatest(loadMessages.type, loadMessagesSaga);
  yield takeLatest(sendMessage.type, sendMessageSaga);
  yield takeLatest(deletePrivateMessage.type, deletePrivateMessageSaga);
  yield takeLatest(readMessages.type, readMessagesSaga);
}